import React, { createContext, useContext, useState, useEffect } from 'react';
import ecom from '../services/ecommerce';

// Define cart item type
export interface CartItem {
  id: number;
  name: string;
  slug?: string;
  category: string;
  price: number;
  quantity: number;
  image: string;
}

// Define cart context type
interface CartContextType {
  items: CartItem[];
  addItem: (item: Omit<CartItem, 'quantity'>, quantity?: number) => void;
  removeItem: (id: number) => void;
  updateQuantity: (id: number, quantity: number) => void;
  clearCart: () => void;
  totalItems: number;
  totalPrice: number;
}

// Create the context
const CartContext = createContext<CartContextType | undefined>(undefined);

// Custom hook to use the cart context
export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

// Constant for localStorage key to avoid typos
const CART_STORAGE_KEY = 'safonova_cart';

// Safe localStorage helper functions
const saveToStorage = (key: string, value: any): void => {
  try {
    if (typeof window !== 'undefined' && typeof window.localStorage !== 'undefined') {
      localStorage.setItem(key, JSON.stringify(value));
    } else {
      // running on server or storage not available — noop
    }
  } catch (error) {
    console.error(`Error saving to localStorage (${key}):`, error);
  }
};

const getFromStorage = (key: string): any => {
  try {
    if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') return null;
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  } catch (error) {
    console.error(`Error reading from localStorage (${key}):`, error);
    return null;
  }
};

// Cart provider component
export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initialize state directly from localStorage to prevent flash of empty cart
  const [items, setItems] = useState<CartItem[]>(() => {
    // Avoid accessing localStorage during SSR — return empty cart on server
    if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') return [];
    const savedCart = getFromStorage(CART_STORAGE_KEY);
    return Array.isArray(savedCart) ? savedCart : [];
  });

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    saveToStorage(CART_STORAGE_KEY, items);
    console.debug(`Cart saved to localStorage: ${items.length} items`);
  }, [items]);

  // Calculate total items
  const totalItems = items.reduce((total, item) => total + item.quantity, 0);

  // Calculate total price
  const totalPrice = items.reduce((total, item) => total + (item.price * item.quantity), 0);

  // Add item to cart
  const addItem = (item: Omit<CartItem, 'quantity'>, quantity = 1) => {
    setItems(currentItems => {
      const existingItemIndex = currentItems.findIndex(cartItem => cartItem.id === item.id);
      
      if (existingItemIndex !== -1) {
        // Item already exists, update quantity
        const updatedItems = [...currentItems];
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          quantity: updatedItems[existingItemIndex].quantity + quantity
        };
        // Push analytics event: added more quantity
        try { ecom.pushAddToCart({ id: item.id, name: item.name, price: item.price, quantity }); } catch(e) {}
        return updatedItems;
      } else {
        // Push analytics event: new item added to cart
        try { ecom.pushAddToCart({ id: item.id, name: item.name, price: item.price, quantity }); } catch(e) {}
        // Item doesn't exist, add new item
        return [...currentItems, { ...item, quantity }];
      }
    });
  };

  // Remove item from cart
  const removeItem = (id: number) => {
    // find item to report removal
    setItems(currentItems => {
      const toRemove = currentItems.find(i => i.id === id);
      if (toRemove) {
        try { ecom.pushRemoveFromCart({ id: toRemove.id, name: toRemove.name, price: toRemove.price, quantity: toRemove.quantity }); } catch(e) {}
      }
      return currentItems.filter(item => item.id !== id);
    });
  };

  // Update item quantity
  const updateQuantity = (id: number, quantity: number) => {
    if (quantity < 1) return;
    
    setItems(currentItems => 
      currentItems.map(item => 
        item.id === id ? { ...item, quantity } : item
      )
    );
  };

  // Clear cart
  const clearCart = () => {
    setItems([]);
    saveToStorage(CART_STORAGE_KEY, []); // Force immediate save on clear
  };

  return (
    <CartContext.Provider value={{
      items,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
      totalItems,
      totalPrice
    }}>
      {children}
    </CartContext.Provider>
  );
};